import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { FormAggregatedResult } from "@/components/form-result/aggregated-result/FormAggregatedResult.tsx";
import { FormAggregatedResultFilterOptions } from "@/components/form-result/aggregated-result/components/types.ts";
import { SurveySummaryChart } from "@/pages/talent/surveys/view/components/SurveySummaryChart.tsx";
import { SurveyQuestionResultsModal } from "@/pages/talent/surveys/view/widgets/SurveyQuestionResultsModal.tsx";
import { useSurveyAggregatedResults, useSurveyChart } from "@/queries/surveys/use-surveys.query.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import {
  ApiSurvey,
  ApiSurveyAggregatedResultsFilter,
  getApiSurveyStatusTitle,
} from "@/types/survey.types.ts";
import { Box, Button, MetricTile, Skeleton, Stack, Title, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export const SurveySummary = ({
  survey,
  show_more = false,
}: {
  survey: ApiSurvey;
  show_more: boolean;
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const library = filtersLibrary();

  const {
    filter: { filter },
    filterButton,
  } = useAppFilter<ApiSurveyAggregatedResultsFilter>({
    filters: [library.department_ids, library.job_profile_ids, library.job_role_ids],
    source: "Survey Results",
    renderFilterButton: ({ onClick }) => (
      <Button size={"lg"} variant={"secondary"} onClick={onClick}>
        {t("common:filter")}
      </Button>
    ),
  });

  const { data: surveyResults, isLoading: isSurveyFormLoading } = useSurveyAggregatedResults(
    survey.id,
    filter
  );

  const { data: surveyChart, isLoading: isSurveyReportLoading } = useSurveyChart(
    survey.id,
    survey.participants_stats.completed_count > 1
  );

  const handleShowDetailModalCallback = (
    element: ApiFormViewElement,
    filterOptions?: FormAggregatedResultFilterOptions
  ) => {
    const resultAggregation = surveyResults?.results.elements.find(
      (el) => el.form_element_id === element.id
    );

    openModal(SurveyQuestionResultsModal, {
      surveyId: survey.id,
      element: element,
      filterResult: filter,
      answerScaleOptionIds: filterOptions?.answer_scale_option_ids,
      answerSelectOptionIds: filterOptions?.answer_select_option_ids,
      selectableOptionIds: filterOptions?.selectable_option_ids,
      resultAggregation: resultAggregation,
      managers: filterOptions?.managers,
      departments: filterOptions?.departments,
    });
  };

  return (
    <>
      <Box className={"py-(--gap-lg)"}>
        <Stack gap={"md"} direction={"horizontal"}>
          <MetricTile
            className={"flex-1"}
            title={t("common:status")}
            value={getApiSurveyStatusTitle(survey.status)}
          />
          <MetricTile
            className={"flex-1"}
            title={t("common:total")}
            value={survey.participants_stats.total_count}
          />
          <MetricTile
            className={"flex-1"}
            title={t("surveys:participant_status_completed")}
            value={survey.participants_stats.completed_count}
          />
          <MetricTile
            className={"flex-1"}
            title={t("surveys:participants_waiting")}
            value={
              survey.participants_stats.invited_count + survey.participants_stats.in_progress_count
            }
          />

          <MetricTile
            className={"flex-1"}
            title={t("common:response_rate")}
            value={
              survey.participants_stats.total_count > 0
                ? (
                    (survey.participants_stats.completed_count /
                      survey.participants_stats.total_count) *
                    100
                  ).toFixed() + "%"
                : "-"
            }
          />
        </Stack>
      </Box>

      <Box className={twMerge("pb-8")}>
        {isSurveyReportLoading && <Skeleton className={"w-full h-60"} />}
        {surveyChart && surveyChart.completed_by_periods.length > 1 && (
          <SurveySummaryChart surveyChart={surveyChart} />
        )}
      </Box>

      <Stack className={"pt-6 container-lg mx-auto"}>
        {survey.participants_stats.completed_count > 0 && (
          <Stack
            direction={"horizontal"}
            gap={"sm"}
            justify={"between"}
            items={"center"}
            className={"ml-20 pt-8"}
          >
            <Title
              header={t("surveys:responses")}
              caption={t("surveys:responses_description", {
                people_answered: t("plurals:people_answered", {
                  count: survey.participants_stats.completed_count,
                }),
              })}
              paddingBottom
            />
            {survey?.anonymous_threshold === 0 ? (
              <Box className={"shrink-0"}>{filterButton()}</Box>
            ) : undefined}
          </Stack>
        )}

        {isSurveyFormLoading && <Skeleton className={"w-full h-10"} />}

        {surveyResults && (
          <Box className={"-ml-14"}>
            <FormAggregatedResult
              form={surveyResults.form}
              results={surveyResults.results}
              showDetailModalCallback={handleShowDetailModalCallback}
              show_more={show_more}
              disableMaxWidth={true}
              hideAISummary={
                (filter.department_ids?.length ?? 0) > 0 ||
                (filter.job_profile_ids?.length ?? 0) > 0 ||
                (filter.job_role_ids?.length ?? 0) > 0
              }
            />
          </Box>
        )}
      </Stack>
    </>
  );
};
